import { useEffect, useRef, useState } from 'react';
import {
  getInitialPortraitPair,
  getNextPortraitIndices,
  getPortraitPairAt,
  hasNextPortraitStep,
  INITIAL_PORTRAIT_INDICES,
  PortraitGroups,
  PortraitIndices,
  PortraitPair,
} from 'src/utils/pick-portrait-pair';

export const PORTRAIT_HOLD_MS = 1500;
export const PORTRAIT_EXIT_DELAY_MS = 180;
export const PORTRAIT_EXIT_MS = 800;
export const PORTRAIT_ENTER_MS = 680;
export const PORTRAIT_TRANSITION_MS = Math.max(PORTRAIT_ENTER_MS, PORTRAIT_EXIT_DELAY_MS + PORTRAIT_EXIT_MS);
const INTRO_ENTER_MS = 300;

function groupsKey(groups: PortraitGroups) {
  return `${groups.left.join('|')}||${groups.right.join('|')}`;
}

function loadImage(src: string) {
  return new Promise<void>((resolve) => {
    const image = new Image();
    image.onload = () => resolve();
    image.onerror = () => resolve();
    image.src = src;
  });
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const EMPTY_GROUPS: PortraitGroups = { left: [], right: [] };

export function usePortraitPairCycle(groups: PortraitGroups = EMPTY_GROUPS) {
  const signature = groupsKey(groups);
  const groupsRef = useRef(groups);
  groupsRef.current = groups;

  const [pair, setPair] = useState<PortraitPair | null>(() => getInitialPortraitPair(groups));
  const [incoming, setIncoming] = useState<PortraitPair | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [cycle, setCycle] = useState(0);
  const pairRef = useRef(pair);
  const indicesRef = useRef<PortraitIndices>(INITIAL_PORTRAIT_INDICES);

  pairRef.current = pair;

  useEffect(() => {
    const nextInitial = getInitialPortraitPair(groupsRef.current);
    const samePair = pairRef.current?.left === nextInitial?.left && pairRef.current?.right === nextInitial?.right;

    indicesRef.current = INITIAL_PORTRAIT_INDICES;

    if (samePair) {
      return;
    }

    setPair(nextInitial);
    setIncoming(null);
    setIsTransitioning(false);
    setCycle(0);
    pairRef.current = nextInitial;
  }, [signature]);

  useEffect(() => {
    const currentGroups = groupsRef.current;

    if (!hasNextPortraitStep(currentGroups) || !pair || isTransitioning) {
      return undefined;
    }

    let cancelled = false;
    const holdMs = cycle === 0 ? INTRO_ENTER_MS + PORTRAIT_HOLD_MS : PORTRAIT_HOLD_MS;
    const holdTimer = window.setTimeout(async () => {
      const nextIndices = getNextPortraitIndices(currentGroups, indicesRef.current);
      const nextPair = getPortraitPairAt(currentGroups, nextIndices);

      if (!nextPair) {
        return;
      }

      await Promise.all([nextPair.left, nextPair.right].filter((src): src is string => Boolean(src)).map(loadImage));

      if (cancelled) {
        return;
      }

      indicesRef.current = nextIndices;

      if (prefersReducedMotion()) {
        setPair(nextPair);
        setCycle((value) => value + 1);
        return;
      }

      setIncoming(nextPair);
      setIsTransitioning(true);
    }, holdMs);

    return () => {
      cancelled = true;
      window.clearTimeout(holdTimer);
    };
  }, [signature, pair, isTransitioning, cycle]);

  useEffect(() => {
    if (!isTransitioning || !incoming) {
      return undefined;
    }

    const doneTimer = window.setTimeout(() => {
      setPair(incoming);
      setIncoming(null);
      setIsTransitioning(false);
      setCycle((value) => value + 1);
    }, PORTRAIT_TRANSITION_MS);

    return () => window.clearTimeout(doneTimer);
  }, [isTransitioning, incoming]);

  return { pair, incoming, isTransitioning };
}
